import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import {
  ArrowLeft, Mail, Lock, User, Phone, ShieldCheck, Eye, EyeOff,
  Check, X as XIcon, Loader2, Users, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Logo } from '@/components/Logo';
import { PasswordStrengthMeter, usePasswordStrength } from '@/components/PasswordStrengthMeter';
import { supabase } from '@/integrations/supabase/client';
import { switchToChildAccount } from '@/lib/switchToChild';
import { setStoredViewMode } from '@/hooks/useViewMode';
import { toast } from 'sonner';

export const PARENT_SIGNUP_ACTIVE_KEY = 'sportara-parent-signup-active';

const TOTAL_STEPS = 5;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const SPORTS = [
  { id: 'soccer', emoji: '⚽', label: 'Soccer' },
  { id: 'football', emoji: '🏈', label: 'Football' },
  { id: 'basketball', emoji: '🏀', label: 'Basketball' },
  { id: 'baseball', emoji: '⚾', label: 'Baseball' },
];

const SKILL_LEVELS = [
  { id: 'beginner', label: 'Brand New', hint: 'Just getting started' },
  { id: 'casual', label: 'Casual Fan', hint: 'Knows the basics' },
  { id: 'enthusiast', label: 'Enthusiast', hint: 'Plays and follows closely' },
];

interface ParentSignupFlowProps {
  onExit: () => void;
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <p className="text-xs text-destructive font-body flex items-center gap-1.5 pl-0.5">
      <XIcon className="w-3.5 h-3.5 shrink-0" /> {msg}
    </p>
  );
}

/** Reassurance line required on every screen of the parent flow. */
function SafetyNote({ className = '' }: { className?: string }) {
  return (
    <p className={`text-[12px] text-muted-foreground font-body flex items-center gap-1.5 justify-center ${className}`}>
      <ShieldCheck className="w-3.5 h-3.5 text-parent shrink-0" />
      Your child's data is safe — we never sell personal information
    </p>
  );
}

export function ParentSignupFlow({ onExit }: ParentSignupFlowProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resuming, setResuming] = useState(true);

  // ── Screen 1: parent account
  const [parentName, setParentName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [accountError, setAccountError] = useState('');
  const passwordStrength = usePasswordStrength(password);

  // ── Screen 2: child profile
  const [childName, setChildName] = useState(() => sessionStorage.getItem('sportara-pending-child-name') || '');
  const [childAge, setChildAge] = useState('');
  const [childSport, setChildSport] = useState('soccer');
  const [childSkill, setChildSkill] = useState('beginner');
  const [childUsername, setChildUsername] = useState('');
  const [usernameStatus, setUsernameStatus] =
    useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [childPin, setChildPin] = useState('');
  const [childError, setChildError] = useState('');
  const [childUserId, setChildUserId] = useState('');

  // ── Screen 3: team code
  const [teamCode, setTeamCode] = useState(() => sessionStorage.getItem('sportara-pending-team-code')?.toLowerCase() || '');
  const [checkingCode, setCheckingCode] = useState(false);
  const [validTeam, setValidTeam] = useState<{ team_name: string; coach_name: string } | null>(null);
  const [codeError, setCodeError] = useState('');
  const [joinedTeam, setJoinedTeam] = useState<{ team_name: string; coach_name: string } | null>(null);

  // ── Screen 4: notification preferences
  const [prefs, setPrefs] = useState({
    weekly_report: true,
    coach_content: true,
    streak_reminders: true,
    milestone_alerts: true,
  });

  useEffect(() => {
    sessionStorage.setItem(PARENT_SIGNUP_ACTIVE_KEY, '1');
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        setEmail(prev => prev || user.email || '');

        const { data: parentProfile } = await (supabase.from('parent_profiles') as any)
          .select('full_name, phone').eq('user_id', user.id).maybeSingle();
        if (parentProfile) {
          setParentName(prev => prev || parentProfile.full_name || '');
          setPhone(prev => prev || parentProfile.phone || '');
        }

        const { data: child } = await (supabase.from('player_profiles') as any)
          .select('player_user_id, username, display_name, sport, skill_level')
          .eq('parent_user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1).maybeSingle();

        if (child && !cancelled) {
          setChildUserId(child.player_user_id);
          setChildUsername(child.username);
          setChildName(child.display_name || child.username);
          setChildSport(child.sport || 'soccer');
          setChildSkill(child.skill_level || 'beginner');
          setStep(3);
        } else if (!cancelled) {
          setStep(2);
        }
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Username is always parent-controlled. They can tap "Suggest one" to get a
  // name-based idea, but nothing is ever written into the field automatically.
  const suggestUsername = () => {
    const base = childName.trim().toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12);
    if (base.length < 2) {
      setChildError("Add your child's name first, then we can suggest a username");
      return;
    }
    setChildError('');
    setChildUsername(`${base}${Math.floor(100 + Math.random() * 900)}`);
  };

  // Backend uniqueness check — debounced, covers both regular accounts and
  // other children's profiles.
  useEffect(() => {
    const u = childUsername.trim().toLowerCase();
    if (!/^[a-z0-9_]{3,20}$/.test(u)) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc('check_username_available', { p_username: u });
      if (error) setUsernameStatus('idle');
      else setUsernameStatus(data === true ? 'available' : 'taken');
    }, 400);
    return () => clearTimeout(t);
  }, [childUsername]);

  // Real-time team code validation.
  useEffect(() => {
    const code = teamCode.trim().toLowerCase();
    setValidTeam(null);
    setCodeError('');
    if (code.length < 4) return;
    setCheckingCode(true);
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc('preview_team_by_code' as any, { p_code: code });
      const team = Array.isArray(data) ? data[0] : null;
      if (team) setValidTeam({ team_name: team.team_name, coach_name: team.coach_name });
      else setCodeError("That code doesn't look right — check with your coach and try again");
      setCheckingCode(false);
    }, 450);
    return () => { clearTimeout(t); setCheckingCode(false); };
  }, [teamCode]);

  const step1Valid = useMemo(
    () => parentName.trim().length >= 2 && EMAIL_RE.test(email) && passwordStrength.score >= 3,
    [parentName, email, passwordStrength.score],
  );
  const step2Valid = useMemo(
    () => childName.trim().length >= 2 && !!childAge && /^[a-z0-9_]{3,20}$/.test(childUsername)
      && usernameStatus === 'available' && /^\d{4,6}$/.test(childPin),
    [childName, childAge, childUsername, usernameStatus, childPin],
  );


  /* ── Screen 1 → create the parent account ── */
  const createParentAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountError('');
    if (!step1Valid) return;
    setLoading(true);
    try {
      const { data: existing } = await supabase.auth.getUser();
      let userId = existing?.user?.id;

      if (!userId) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: parentName.trim(), user_persona: 'parent', primary_role: 'parent' },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        userId = data.user?.id;
        if (!data.session) {
          // Sign in immediately so the rest of the flow can write child data.
          const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
          if (signInErr) throw new Error('Account created — please confirm your email, then sign in to finish setup.');
        }
      }
      if (!userId) throw new Error('Could not create your account');

      await (supabase.from('parent_profiles') as any).upsert(
        { user_id: userId, full_name: parentName.trim(), email, phone: phone.trim() || null },
        { onConflict: 'user_id' },
      );
      await supabase.from('profiles')
        .update({ display_name: parentName.trim(), user_persona: 'parent', primary_role: 'parent' } as any)
        .eq('user_id', userId);

      setStep(2);
    } catch (err: any) {
      setAccountError(err.message || 'Could not create your account');
    } finally {
      setLoading(false);
    }
  };

  /* ── Screen 2 → create the child account ── */
  const createChild = async (e: React.FormEvent) => {
    e.preventDefault();
    setChildError('');
    if (!step2Valid) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-players', {
        body: {
          action: 'create',
          username: childUsername,
          display_name: childName.trim(),
          pin: childPin,
          sport: childSport,
          skill_level: childSkill,
          age: Number(childAge) || null,
        },
      });
      if (error) throw new Error('Could not create your child’s profile');
      if (data?.error) throw new Error(data.error);
      setChildUserId(data?.player?.id || '');
      setStep(3);
    } catch (err: any) {
      setChildError(err.message || 'Could not create your child’s profile');
    } finally {
      setLoading(false);
    }
  };

  /* ── Screen 3 → join a team ── */
  const joinTeam = async () => {
    if (!validTeam || !childUserId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('parent_join_child_to_team' as any, {
        p_child_user_id: childUserId,
        p_code: teamCode.trim().toLowerCase(),
      });
      if (error) throw error;
      const res = data as any;
      if (res?.error) { setCodeError(res.error); return; }
      setJoinedTeam(validTeam);
      sessionStorage.removeItem('sportara-pending-team-code'); sessionStorage.removeItem('sportara-pending-child-name');
      setStep(4);
    } catch (err: any) {
      setCodeError(err.message || 'Could not join that team');
    } finally {
      setLoading(false);
    }
  };

  /* ── Screen 4 → save preferences and celebrate ── */
  const savePrefs = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await (supabase.from('notification_preferences') as any).upsert(
          { parent_user_id: user.id, child_player_id: childUserId || null, ...prefs },
          { onConflict: 'parent_user_id,child_player_id' },
        );
        supabase.functions.invoke('send-parent-welcome-email', {
          body: {
            parent_name: parentName,
            child_name: childName,
            child_username: childUsername,
            team_name: joinedTeam?.team_name ?? null,
            coach_name: joinedTeam?.coach_name ?? null,
          },
        }).catch(() => undefined);
      }
      setStep(5);
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 } });
    } finally {
      setLoading(false);
    }
  };

  const goToDashboard = () => {
    setStoredViewMode('player');
    sessionStorage.removeItem(PARENT_SIGNUP_ACTIVE_KEY);
    const pending = sessionStorage.getItem('sportara-pending-team-code');
    window.location.href = pending ? `/join/${encodeURIComponent(pending)}` : '/';
  };

  const switchToChild = async () => {
    sessionStorage.removeItem(PARENT_SIGNUP_ACTIVE_KEY);
    toast.info(`Sign in as @${childUsername} with the PIN you set to see ${childName}'s view.`);
    await switchToChildAccount(childUsername);
  };

  const back = () => {
    if (step === 1) { sessionStorage.removeItem(PARENT_SIGNUP_ACTIVE_KEY); onExit(); return; }
    setStep(s => Math.max(1, s - 1));
  };

  if (resuming) {
    return (
      <div className="min-h-screen premium-page-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-parent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen premium-page-bg flex flex-col">
      <div className="px-4 pt-8 pb-4 text-center">
        <Logo className="w-12 h-12 mx-auto" alt="Sportara" />
        <p className="text-[11px] font-display font-bold uppercase tracking-[0.2em] text-parent mt-2">
          Parent Setup
        </p>
      </div>

      <div className="flex-1 flex justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border-2 border-parent/25 bg-card/70 backdrop-blur-xl p-5 sm:p-7 shadow-xl">
            {/* Progress + back */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={back}
                className="w-9 h-9 rounded-full bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
                aria-label="Go back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-display font-bold uppercase tracking-wider text-parent">
                    Step {step} of {TOTAL_STEPS}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-body">Parent setup</p>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <motion.div
                    className="h-full bg-parent rounded-full"
                    animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                    transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                  />
                </div>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {/* ═══ Screen 1 — Parent account ═══ */}
              {step === 1 && (
                <motion.form
                  key="s1" onSubmit={createParentAccount}
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
                  className="space-y-4"
                >
                  <div>
                    <h1 className="font-display font-bold text-2xl">Create your parent account</h1>
                    <p className="text-sm text-muted-foreground font-body mt-1">
                      You'll use this to track your child's progress.
                    </p>
                  </div>

                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={parentName} onChange={e => setParentName(e.target.value)}
                      placeholder="Your full name" required
                      className="pl-10 h-12 rounded-xl border-2 font-body"
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="Your email address" required
                      className="pl-10 h-12 rounded-xl border-2 font-body"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Password" required minLength={8}
                      className="pl-10 pr-10 h-12 rounded-xl border-2 font-body"
                    />
                    <button
                      type="button" onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <PasswordStrengthMeter password={password} show={!!password} />
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="Phone number (optional)"
                      className="pl-10 h-12 rounded-xl border-2 font-body"
                    />
                  </div>

                  <FieldError msg={accountError} />
                  <SafetyNote />

                  <Button
                    type="submit" disabled={!step1Valid || loading}
                    className="w-full h-12 rounded-xl font-display font-bold text-base bg-parent text-parent-foreground hover:bg-parent/90"
                  >
                    {loading ? 'Creating your account...' : 'Create My Parent Account'}
                  </Button>
                </motion.form>
              )}

              {/* ═══ Screen 2 — Child profile ═══ */}
              {step === 2 && (
                <motion.form
                  key="s2" onSubmit={createChild}
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
                  className="space-y-4"
                >
                  <div>
                    <h1 className="font-display font-bold text-2xl">Your child's profile</h1>
                    <p className="text-sm text-muted-foreground font-body mt-1">
                      First name only — we never ask for your child's last name.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      value={childName} onChange={e => setChildName(e.target.value)}
                      placeholder="Child's first name" required
                      className="h-12 rounded-xl border-2 font-body flex-1"
                    />
                    <Input
                      type="number" min={4} max={18} value={childAge}
                      onChange={e => setChildAge(e.target.value)}
                      placeholder="Age" required
                      className="h-12 rounded-xl border-2 font-body w-24"
                    />
                  </div>

                  <div>
                    <p className="text-sm font-display font-bold mb-2">Primary sport</p>
                    <div className="grid grid-cols-2 gap-2">
                      {SPORTS.map(s => (
                        <button
                          key={s.id} type="button" onClick={() => setChildSport(s.id)}
                          className={`h-11 rounded-xl border-2 text-sm font-display font-bold transition-all ${
                            childSport === s.id ? 'border-parent bg-parent/10' : 'border-border text-muted-foreground'
                          }`}
                        >
                          {s.emoji} {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-display font-bold mb-2">Skill level</p>
                    <div className="space-y-2">
                      {SKILL_LEVELS.map(l => (
                        <button
                          key={l.id} type="button" onClick={() => setChildSkill(l.id)}
                          className={`w-full text-left px-4 py-2.5 rounded-xl border-2 transition-all ${
                            childSkill === l.id ? 'border-parent bg-parent/10' : 'border-border'
                          }`}
                        >
                          <p className="font-display font-bold text-sm">{l.label}</p>
                          <p className="text-[11px] text-muted-foreground font-body">{l.hint}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-display font-bold">Username</p>
                      <button
                        type="button" onClick={suggestUsername}
                        className="text-[12px] font-display font-bold text-parent hover:underline"
                      >
                        Suggest one
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        value={childUsername}
                        onChange={e => setChildUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20))}
                        placeholder="Pick a username (or tap Suggest one)" required
                        className="h-12 rounded-xl border-2 font-body pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        {usernameStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                        {usernameStatus === 'available' && <Check className="w-4 h-4 text-parent" />}
                        {usernameStatus === 'taken' && <XIcon className="w-4 h-4 text-destructive" />}
                      </span>
                    </div>
                    <p className={`text-[11px] font-body ${usernameStatus === 'taken' ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {usernameStatus === 'taken'
                        ? 'That username is already taken — try another.'
                        : usernameStatus === 'available'
                          ? 'That username is available.'
                          : '3-20 characters — lowercase letters, numbers, underscores.'}
                    </p>

                  </div>

                  <Input
                    value={childPin} inputMode="numeric"
                    onChange={e => setChildPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="4-digit login PIN" required
                    className="h-12 rounded-xl border-2 font-body tracking-[0.3em]"
                  />
                  <p className="text-[12px] text-muted-foreground font-body">
                    Your child will use this username and PIN to log in on their device.
                  </p>

                  <FieldError msg={childError} />
                  <SafetyNote />

                  <Button
                    type="submit" disabled={!step2Valid || loading}
                    className="w-full h-12 rounded-xl font-display font-bold text-base bg-parent text-parent-foreground hover:bg-parent/90"
                  >
                    {loading ? "Setting up..." : "Set Up My Child's Profile"}
                  </Button>
                </motion.form>
              )}

              {/* ═══ Screen 3 — Team code ═══ */}
              {step === 3 && (
                <motion.div
                  key="s3"
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
                  className="space-y-4"
                >
                  <div>
                    <h1 className="font-display font-bold text-2xl">
                      Has your coach shared a Team Code with you?
                    </h1>
                    <p className="text-sm text-muted-foreground font-body mt-1">
                      Enter it below to connect {childName || 'your child'} to their team.
                    </p>
                  </div>

                  <div className="relative">
                    <Input
                      value={teamCode}
                      onChange={e => setTeamCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12))}
                      placeholder="TEAM CODE"
                      className={`h-16 rounded-2xl border-2 text-center text-2xl font-display font-bold tracking-[0.3em] uppercase ${
                        validTeam ? 'border-parent' : codeError ? 'border-destructive' : ''
                      }`}
                    />
                    {checkingCode && (
                      <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  {validTeam && (
                    <p className="text-sm font-body text-parent flex items-center gap-2">
                      <Check className="w-4 h-4 shrink-0" />
                      You're joining {validTeam.team_name} coached by {validTeam.coach_name}!
                    </p>
                  )}
                  <FieldError msg={codeError} />

                  <Button
                    onClick={joinTeam} disabled={!validTeam || loading}
                    className="w-full h-12 rounded-xl font-display font-bold text-base bg-parent text-parent-foreground hover:bg-parent/90"
                  >
                    {loading ? 'Joining...' : 'Join Team'}
                  </Button>
                  <button
                    onClick={() => setStep(4)}
                    className="w-full text-sm text-muted-foreground hover:text-foreground font-body"
                  >
                    Skip for now
                  </button>

                  <SafetyNote className="pt-1" />
                </motion.div>
              )}

              {/* ═══ Screen 4 — Notification preferences ═══ */}
              {step === 4 && (
                <motion.div
                  key="s4"
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
                  className="space-y-4"
                >
                  <div>
                    <h1 className="font-display font-bold text-2xl">
                      How would you like to stay updated on {childName || 'your child'}'s progress?
                    </h1>
                  </div>

                  <div className="space-y-2">
                    {([
                      ['weekly_report', 'Weekly progress report email'],
                      ['coach_content', 'New coach content notifications'],
                      ['streak_reminders', 'Streak reminder notifications'],
                      ['milestone_alerts', 'Milestone and achievement alerts'],
                    ] as const).map(([key, label]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between gap-3 rounded-xl border-2 border-border px-4 py-3"
                      >
                        <p className="font-body text-sm">{label}</p>
                        <Switch
                          checked={prefs[key]}
                          onCheckedChange={v => setPrefs(p => ({ ...p, [key]: v }))}
                          aria-label={label}
                        />
                      </div>
                    ))}
                  </div>

                  <p className="text-[12px] text-muted-foreground font-body">
                    You can change these anytime in your settings.
                  </p>
                  <SafetyNote />

                  <Button
                    onClick={savePrefs} disabled={loading}
                    className="w-full h-12 rounded-xl font-display font-bold text-base bg-parent text-parent-foreground hover:bg-parent/90"
                  >
                    {loading ? 'Saving...' : 'Almost Done!'}
                  </Button>
                </motion.div>
              )}

              {/* ═══ Screen 5 — All set ═══ */}
              {step === 5 && (
                <motion.div
                  key="s5"
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  className="space-y-5 text-center"
                >
                  <div className="w-16 h-16 rounded-2xl bg-parent/15 flex items-center justify-center mx-auto">
                    <Sparkles className="w-8 h-8 text-parent" />
                  </div>
                  <h1 className="font-display font-bold text-2xl">
                    {childName || 'Your child'} is all set! 🎉
                  </h1>

                  <div className="rounded-2xl border-2 border-parent/25 bg-parent/5 p-4 text-left space-y-2">
                    <div className="flex justify-between gap-3">
                      <span className="text-sm text-muted-foreground font-body">Username</span>
                      <span className="font-display font-bold text-sm">@{childUsername}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-sm text-muted-foreground font-body">Sport</span>
                      <span className="font-display font-bold text-sm capitalize">{childSport}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-sm text-muted-foreground font-body">Team</span>
                      <span className="font-display font-bold text-sm">{joinedTeam?.team_name ?? 'Not joined yet'}</span>
                    </div>
                    {joinedTeam && (
                      <div className="flex justify-between gap-3">
                        <span className="text-sm text-muted-foreground font-body">Coach</span>
                        <span className="font-display font-bold text-sm">{joinedTeam.coach_name}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={goToDashboard}
                      className="w-full h-12 rounded-xl font-display font-bold text-base bg-parent text-parent-foreground hover:bg-parent/90"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      View My Parent Dashboard
                    </Button>
                    <Button
                      onClick={switchToChild} variant="outline"
                      className="w-full h-12 rounded-xl font-display font-bold text-base border-2"
                    >
                      Switch to {childName || 'my child'}'s Account
                    </Button>
                  </div>

                  <SafetyNote />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentSignupFlow;
